import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Grid,
  Button,
  Modal,
  Typography,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getBooking, acceptBooking } from 'api';

// Import icons
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LabelIcon from '@mui/icons-material/Label';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CodeIcon from '@mui/icons-material/Code';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

// Styled container
const Container = styled(Box)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  padding: theme.spacing(3)
}));

// Styled Modal component
const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
}));

// Detail Item component
const DetailItem = ({ icon, label, value }) => (
  <Grid item xs={12} md={4}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: 1,
        backgroundColor: 'background.paper',
        borderRadius: 1,
      }}
    >
      {icon}
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1" color="text.primary">
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

const BookingDetailList = () => {
  const { slug } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const result = queryParams.get('result');

  // State
  const [theBooking, setBooking] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState({});
  const [actionInProgress, setActionInProgress] = useState(false);

  // Type and status mappings
  const typeMapping = {
    HD: 'Hot Desk',
    PR: 'Private Office',
    MR: 'Meeting Room',
    EV: 'Event',
    OT: 'Other'
  };

  const statusMapping = {
    PN: 'Pending',
    AC: 'Accepted',
    RJ: 'Rejected',
    CA: 'Cancelled'
  };

  // Get status chip
  const getStatusChip = (status) => {
    const statusConfig = {
      PN: { color: 'warning', label: 'Pending' },
      AC: { color: 'success', label: 'Accepted' },
      RJ: { color: 'error', label: 'Rejected' },
      CA: { color: 'default', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || { color: 'default', label: status };
    
    return (
      <Chip 
        color={config.color} 
        label={config.label} 
        size="small"
        sx={{ fontWeight: 'medium' }}
      />
    );
  };

  function doAcceptBooking(slug, action) {
    setConfirmAction({ slug, action });
    setOpenConfirm(true);
  }

  function doConfirm() {
    setActionInProgress(true);
    acceptBooking(confirmAction)
      .then(() => {
        fetchBooking();
        setOpenConfirm(false);
        setSuccess(`Booking ${confirmAction.action === 'accept' ? 'accepted' : 'rejected'} successfully`);
        setTimeout(() => setSuccess(null), 5000);
      })
      .catch(error => {
        console.error('Error processing booking:', error);
        setError(`Failed to ${confirmAction.action} booking. Please try again.`);
        setTimeout(() => setError(null), 5000);
      })
      .finally(() => {
        setActionInProgress(false);
      });
  }

  function fetchBooking() {
    setIsLoading(true);
    getBooking(slug)
      .then(response => {
        setBooking(response);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching booking:', error);
        setError('Failed to load booking details. Please try again.');
        setIsLoading(false);
      });
  }

  useEffect(() => {
    if (result === 'accept' || result === 'reject') {
      doAcceptBooking(slug, result);
    }
  }, [result, slug]);

  useEffect(() => {
    fetchBooking();
  }, [slug]);

  // Format booking data
  const formattedDate = theBooking?.planned_start_day ? new Date(theBooking.planned_start_day).toLocaleDateString('en-GB') : '—';
  const formattedStartTime = theBooking?.planned_start_time ? theBooking.planned_start_time.slice(0, -3) : '—';
  const formattedEndTime = theBooking?.planned_end_time ? theBooking.planned_end_time.slice(0, -3) : '—';
  const bookingPrice = theBooking?.price ? `£${parseFloat(theBooking.price).toFixed(2)}` : '—';
  const bookingPriceWithVAT = theBooking?.price && theBooking?.vat_amount 
    ? `£${(parseFloat(theBooking.price) + parseFloat(theBooking.vat_amount)).toFixed(2)}` 
    : '—';

  if (isLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Paper elevation={3} sx={{ padding: 3, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" color="primary">
            Booking Details
          </Typography>
          {theBooking?.status && getStatusChip(theBooking.status)}
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          <DetailItem 
            icon={<CodeIcon color="primary" />} 
            label="Booking Code" 
            value={theBooking?.code} 
          />
          
          <DetailItem 
            icon={<CalendarTodayIcon color="primary" />} 
            label="Date" 
            value={formattedDate} 
          />
          
          <DetailItem 
            icon={<AccessTimeIcon color="primary" />} 
            label="Time" 
            value={`${formattedStartTime} - ${formattedEndTime}`} 
          />
          
          <DetailItem 
            icon={<LabelIcon color="primary" />} 
            label="Type" 
            value={typeMapping[theBooking?.booking_type] || theBooking?.booking_type} 
          />
          
          <DetailItem 
            icon={<MonetizationOnIcon color="primary" />} 
            label="Price (excl. VAT)" 
            value={bookingPrice} 
          />
          
          <DetailItem 
            icon={<MonetizationOnIcon color="primary" />} 
            label="Price (incl. VAT)" 
            value={bookingPriceWithVAT} 
          />
          
          <DetailItem 
            icon={<StorefrontIcon color="primary" />} 
            label="Location" 
            value={theBooking?.venue?.name} 
          />
          
          <DetailItem 
            icon={<PersonIcon color="primary" />} 
            label="Member" 
            value={typeof theBooking?.member?.user?.first_name === 'string' ? 
                   `${theBooking?.member?.user?.first_name || ''} ${theBooking?.member?.user?.last_name || ''}`.trim() : 
                   typeof theBooking?.tenant === 'string' ? theBooking?.tenant : '—'} 
          />
          
          <DetailItem 
            icon={<BookmarkIcon color="primary" />} 
            label="Status" 
            value={statusMapping[theBooking?.status] || theBooking?.status} 
          />
        </Grid>
        
        {theBooking?.description && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Booking Notes
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ p: 2, backgroundColor: 'background.default' }}
            >
              <Typography variant="body2">
                {theBooking.description}
              </Typography>
            </Paper>
          </>
        )}
      </Paper>
      
      {/* Action buttons only visible for pending bookings */}
      {theBooking?.status === 'PN' && (
        <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Booking Request Action
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please review the booking details above and decide whether to accept or reject this booking request.
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              onClick={() => doAcceptBooking(theBooking?.slug, 'accept')}
              fullWidth={true}
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'success.main',
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: '20px',
                padding: '10px 24px',
                maxWidth: { xs: '100%', sm: '240px' },
                '&:hover': {
                  backgroundColor: 'success.lighter',
                  borderColor: 'success.main',
                }
              }}
              startIcon={<CheckIcon />}
            >
              Accept Booking
            </Button>

            <Button
              onClick={() => doAcceptBooking(theBooking?.slug, 'reject')}
              fullWidth={true}
              variant="outlined"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'error.main',
                border: '1px solid',
                borderColor: 'error.main',
                borderRadius: '20px',
                padding: '10px 24px',
                maxWidth: { xs: '100%', sm: '240px' },
                '&:hover': {
                  backgroundColor: 'error.lighter',
                  borderColor: 'error.main',
                }
              }}
              startIcon={<CloseIcon />}
            >
              Reject Booking
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Success message for already processed bookings */}
      {theBooking?.status === 'AC' && (
        <Paper 
          elevation={3} 
          sx={{ 
            padding: 3, 
            borderRadius: 2,
            backgroundColor: 'success.lighter',
            border: '1px solid',
            borderColor: 'success.light'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ color: 'success.main', mr: 2, fontSize: '2rem' }} />
            <Box>
              <Typography variant="h6" color="success.dark" gutterBottom>
                Booking Already Accepted
              </Typography>
              <Typography variant="body2">
                This booking has already been accepted and confirmed with the customer.
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
      
      {theBooking?.status === 'RJ' && (
        <Paper 
          elevation={3} 
          sx={{ 
            padding: 3, 
            borderRadius: 2,
            backgroundColor: 'error.lighter',
            border: '1px solid',
            borderColor: 'error.light'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CancelIcon sx={{ color: 'error.main', mr: 2, fontSize: '2rem' }} />
            <Box>
              <Typography variant="h6" color="error.dark" gutterBottom>
                Booking Already Rejected
              </Typography>
              <Typography variant="body2">
                This booking has already been rejected.
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Confirmation Modal */}
      <Modal
        open={openConfirm}
        onClose={() => !actionInProgress && setOpenConfirm(false)}
        aria-labelledby="confirmation-modal-title"
        aria-describedby="confirmation-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ModalContent>
          <Typography id="confirmation-modal-title" variant="h6" component="h2" gutterBottom>
            {confirmAction.action === 'accept' ? 'Accept Booking' : 'Reject Booking'}
          </Typography>
          
          <Typography id="confirmation-modal-description" sx={{ mt: 2, mb: 3 }}>
            Are you sure you want to {confirmAction.action === 'accept' ? 'accept' : 'reject'} this booking request?
            {confirmAction.action === 'reject' && ' The customer will be notified that their booking has been rejected.'}
          </Typography>
          
          <Box display="flex" justifyContent="space-between">
            <Button 
              variant="outlined" 
              onClick={() => setOpenConfirm(false)}
              disabled={actionInProgress}
              sx={{ borderRadius: '20px' }}
            >
              Cancel
            </Button>
            
            <Button 
              variant="contained" 
              color={confirmAction.action === 'accept' ? 'success' : 'error'}
              onClick={doConfirm}
              disabled={actionInProgress}
              sx={{ borderRadius: '20px' }}
              startIcon={actionInProgress ? 
                <CircularProgress size={20} color="inherit" /> : 
                (confirmAction.action === 'accept' ? <CheckIcon /> : <CloseIcon />)
              }
            >
              {actionInProgress ? 'Processing...' : (confirmAction.action === 'accept' ? 'Confirm Accept' : 'Confirm Reject')}
            </Button>
          </Box>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default BookingDetailList;